import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAuth } from 'src/context/auth';
import useAuthorization from 'src/hooks/authorization/useAuthorization';
import { PROFILE_CUSTOMIZATION_READ, TARGET_STRATEGIES_READ } from 'src/components/PermissionGuard/checks';
import { useGetHexReportsQuery, useGetCompanyByIdQuery } from 'src/api/redux';
import { useGetProviderStrategyTabsQuery } from 'src/api/redux/strategy/chainedStrategiesApi';
import { getProfileUrl, slugToProfileType } from 'src/utils/profileUrl';

const BASE_CATEGORIES = {
  affiliations: 'Providers',
  'payer-mix': 'Payer Insights',
  patient_patterns: 'Referrals',
  referrals: '2015 Inbound Referrals',
  old_affiliations: 'Affiliations',
  apc: 'APC Procedures',
  cpt: 'CPT® Procedures',
  drg: 'DRG Procedures',
  icd: 'Diagnoses',
  icdp: 'ICD Procedures',
  hcpcs: 'HCPCS Level II',
  prescriptions: 'Prescriptions',
  payments: 'Open Payments',
  education: 'Education Network',
  relationships: 'Relationships',
  publications: 'Publications',
  system_sites: 'Sites',
} as const;

const PROCEDURE_CATEGORIES = ['drg', 'apc', 'cpt', 'icdp', 'hcpcs'] as const;

export type TabChild = {
  id: string;
  label: string;
  path: string;
};

export type ProfileTab = {
  id: string;
  label: string;
  path: string;
  type: 'link' | 'dropdown';
  pinned: boolean;
  children?: TabChild[];
  isStrategy?: boolean;
};

type UseMenuContentProps = {
  id: string;
  type: string;
  categories: Record<string, boolean> | undefined;
  hasPublications: boolean;
  providerId?: string;
};

/**
 * Builds the full ordered list of profile tabs.
 * No viewport/breakpoint logic — overflow is handled by useOverflowTabs.
 * Strategy tabs are excluded when profile management is on (plays_enabled plus
 * the seat's profile customization permission), or when the seat can't read
 * target strategies.
 */
const useMenuContent = ({
  id,
  type,
  categories,
  hasPublications,
  providerId,
}: UseMenuContentProps): ProfileTab[] => {
  const { user } = useAuth();
  const companyId = user.company.id?.toString();
  const { data: company } = useGetCompanyByIdQuery(
    { companyId },
    { skip: !companyId }
  );

  const shouldFetchReports = !!type && type !== 'system';
  const { data: reportsData } = useGetHexReportsQuery(
    { profile_type: slugToProfileType(type), entity_id: id },
    { skip: !shouldFetchReports }
  );

  const { useSystemProfiles, useHexReports, profileStrategies } = useFlags();

  const { hasPermission } = useAuthorization();
  const isProfileManagementEnabled =
    !!user?.plays_enabled && hasPermission(PROFILE_CUSTOMIZATION_READ);
  const canReadStrategies = hasPermission(TARGET_STRATEGIES_READ);

  const { data: strategyTabsResponse } = useGetProviderStrategyTabsQuery(
    { providerId: providerId || '' },
    {
      skip:
        !profileStrategies ||
        !providerId ||
        isProfileManagementEnabled ||
        !canReadStrategies,
    }
  );

  return useMemo(() => {
    if (!categories) return [];

    const getCategoryTitle = (key: string) => {
      const overrides: Record<string, string> = {
        patient_patterns: company?.referrals_tab_name || 'Referrals',
      };
      return (
        overrides[key] ??
        BASE_CATEGORIES[key as keyof typeof BASE_CATEGORIES] ??
        key
      );
    };

    const isGroupPac = type === 'hcp-group' || type === 'group_pac';
    const isSystem = type === 'system';
    const hasReports = useHexReports && (reportsData?.results?.length ?? 0) > 0;

    // --- Pinned tabs (always visible, never collapse) ---
    const pinned: ProfileTab[] = [];

    if (isProfileManagementEnabled) {
      pinned.push({
        id: 'plays',
        label: 'My Insights',
        path: getProfileUrl(type, id, 'plays'),
        type: 'link',
        pinned: true,
      });
    }

    pinned.push(
      {
        id: 'overview',
        label: 'Overview',
        path: getProfileUrl(type, id),
        type: 'link',
        pinned: true,
      },
      {
        id: 'activity',
        label: 'Activity',
        path: getProfileUrl(type, id, 'activity'),
        type: 'link',
        pinned: true,
      }
    );

    // --- Strategy tabs (excluded when plays_enabled is on) ---
    const strategyTabs: ProfileTab[] = [];
    if (
      profileStrategies &&
      !isProfileManagementEnabled &&
      strategyTabsResponse?.results?.length
    ) {
      const chains = strategyTabsResponse.results;
      if (chains.length <= 2) {
        // Show as individual link tabs
        chains.forEach((chain: any) => {
          strategyTabs.push({
            id: `strategy-${chain.id}`,
            label: chain.label,
            path: getProfileUrl(type, id, `strategy/${chain.id}`),
            type: 'link',
            pinned: false,
            isStrategy: true,
          });
        });
      } else {
        // Collapse into a single dropdown
        strategyTabs.push({
          id: 'strategies',
          label: 'Strategies',
          path: '#',
          type: 'dropdown',
          pinned: false,
          isStrategy: true,
          children: chains.map((chain: any) => ({
            id: `strategy-${chain.id}`,
            label: chain.label,
            path: getProfileUrl(type, id, `strategy/${chain.id}`),
          })),
        });
      }
    }

    // --- Collapsible tabs ---
    const collapsible: ProfileTab[] = [];

    // Procedures dropdown
    const availableProcedures = PROCEDURE_CATEGORIES.filter(
      (proc) => !!categories[proc]
    );
    if (availableProcedures.length > 0) {
      collapsible.push({
        id: 'procedure',
        label: 'Procedures',
        path: getProfileUrl(type, id, 'drg'),
        type: 'dropdown',
        pinned: false,
        children: availableProcedures.map((proc) => ({
          id: proc,
          label: getCategoryTitle(proc),
          path: getProfileUrl(type, id, proc),
        })),
      });
    }

    // Reports dropdown
    if (hasReports) {
      collapsible.push({
        id: 'reports',
        label: 'Reports',
        path: '#',
        type: 'dropdown',
        pinned: false,
        children: reportsData!.results.map((report: any) => ({
          id: String(report.id),
          label: report.label,
          path: getProfileUrl(type, id, String(report.id)),
        })),
      });
    }

    // Payer Insights
    if (!isGroupPac && !isSystem) {
      collapsible.push({
        id: 'payer-mix',
        label: getCategoryTitle('payer-mix'),
        path: getProfileUrl(type, id, 'payer-mix'),
        type: 'link',
        pinned: false,
      });
    }

    // Remaining data-driven categories
    const remaining = Object.keys(BASE_CATEGORIES)
      .filter((view) => {
        if (view === 'publications') return type === 'hcp' && hasPublications;
        if (view === 'payer-mix') return !isGroupPac && !useSystemProfiles;
        if (PROCEDURE_CATEGORIES.includes(view as any)) return false;
        return (
          !!categories[view] ||
          !!categories[`inbound_${view}`] ||
          !!categories[`outbound_${view}`]
        );
      })
      .map((view): ProfileTab => {
        if (view === 'old_affiliations') {
          return {
            id: 'group-affiliations',
            label: getCategoryTitle(view),
            path: getProfileUrl(type, id, 'group-affiliations'),
            type: 'link',
            pinned: false,
          };
        }
        if (view === 'system_sites') {
          return {
            id: 'system-sites',
            label: 'Sites',
            path: getProfileUrl(type, id, 'system-sites'),
            type: 'link',
            pinned: false,
          };
        }
        return {
          id: view,
          label: getCategoryTitle(view),
          path: getProfileUrl(type, id, view),
          type: 'link',
          pinned: false,
        };
      });

    collapsible.push(...remaining);

    return [...pinned, ...strategyTabs, ...collapsible];
  }, [
    categories,
    company?.referrals_tab_name,
    hasPublications,
    id,
    profileStrategies,
    reportsData,
    strategyTabsResponse,
    type,
    useHexReports,
    isProfileManagementEnabled,
    useSystemProfiles,
  ]);
};

export default useMenuContent;
